import React, { useMemo } from "react";
import Loader from "../../../components/Loader/Loader";
import { safeDivide } from "../dashboardMetrics";
import {
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import dayjs from "dayjs";

const OrganizationBigTable = ({ dashboardBigTableData, isLoading }) => {
	// const currentMonth = dayjs().format("MM");
	const currentMonth = dayjs().month() + 1;
	// const currentDate = dayjs().format("DD");
	const currentDate = dayjs().date();
	const currentYear = dayjs().year();

	/*
	 * Pre-aggregate every member's loads ONCE.
	 *
	 * Each member's loadsMonth was walked four separate times — once per
	 * column group — and every walk called dayjs(load.createdAt) on every load.
	 * dayjs parsing is not cheap, so the cost was
	 * 4 x members x loads parses on EVERY render, and the component re-renders
	 * whenever anything on the dashboard changes.
	 *
	 * One pass builds what all the columns need, memoised on the data so it
	 * only recomputes when the data itself changes. Date parts are read from a
	 * plain Date rather than dayjs — same result, far less work.
	 */
	const statsByMember = useMemo(() => {
		const map = new Map();
		const branches = dashboardBigTableData?.branches;
		if (!Array.isArray(branches)) return map;

		for (const branch of branches) {
			for (const member of branch?.members || []) {
				let monthProfit = 0;
				let monthLoads = 0;
				let todayProfit = 0;
				let todayLoads = 0;

				for (const load of member?.loadsMonth || []) {
					const d = new Date(load.createdAt);
					if (d.getFullYear() !== currentYear || d.getMonth() + 1 !== currentMonth) continue;

					const profit = (Number(load.customerRate) || 0) - (Number(load.driverRate) || 0);
					monthProfit += profit;
					monthLoads += 1;

					if (d.getDate() === currentDate) {
						todayProfit += profit;
						todayLoads += 1;
					}
				}

				map.set(member?._id, { monthProfit, monthLoads, todayProfit, todayLoads });
			}
		}
		return map;
	}, [dashboardBigTableData, currentYear, currentMonth, currentDate]);

	if (isLoading) {
		return <Loader />;
	}

	let monthlyProfitColl = [];
	let personTotalProfit = 0;
	let personTotalLoad = 0;
	let personDailyProfit = 0;
	let personDailyLoad = 0;
	let orgProfitToday = 0;
	let orgLoadToday = 0;

	return (
		<div>
			<TableContainer component={Paper}>
				<Table size="small" aria-label="a dense table">
					<TableHead>
						<TableRow sx={{ backgroundColor: "#323A46", color: "white" }}>
							{/* <TableCell></TableCell> */}
							<TableCell sx={{ color: "white" }}>Name</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Current
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Goals
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Left
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Daily Goals
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Profit Today
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Left Today
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Loads Today
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Avg. Profit Per Load
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Avg. Per Day
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Pace
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Best Month
							</TableCell>
							<TableCell sx={{ color: "white" }} align="left">
								Comparison vs Best Month
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{dashboardBigTableData?.branches?.map((branch) => {
							let totalBranchGoals = 0;
							let totalActiveDays = 0;
							let totalProfit = 0;
							let totalLoads = 0;

							branch?.members?.forEach((member) => {
								member?.loadsMonth?.forEach((load) => {
									const loadDate = dayjs(load.createdAt);
									if (loadDate.year() === currentYear && loadDate.month() + 1 === currentMonth) {
										const profit = parseInt(load.customerRate) - parseInt(load.driverRate);
										totalProfit += profit;
										totalLoads += 1;
									}
								});

								member?.task?.forEach((tsk) => {
									if (tsk?.year === currentYear) {
										const currentMonthData = tsk?.months?.find(
											(month) => month?.monthNumber === currentMonth
										);
										if (currentMonthData) {
											totalBranchGoals += currentMonthData.goals;
											totalActiveDays += currentMonthData.activeDays;
										}
									}
								});
							});

							return (
								<React.Fragment key={branch._id}>
									{branch?.members?.map((member) => (
										<TableRow
											key={member._id}
											sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
										>
											{/* Member Name */}
											<TableCell component="th" scope="row">
												{member?.name}
											</TableCell>

											{/* Current Profit */}
											{(() => {
												personTotalProfit = 0;
												personTotalLoad = 0;

												member?.loadsMonth?.forEach((load) => {
													const loadDate = dayjs(load.createdAt);
													if (
														loadDate.year() === currentYear &&
														loadDate.month() + 1 === currentMonth
													) {
														const profit = parseInt(load.customerRate) - parseInt(load.driverRate);
														personTotalProfit += profit;
														personTotalLoad += 1;
													}
												});

												return <TableCell>$ {personTotalProfit}</TableCell>; // Return the total profit after summing
											})()}

											{/* Goals + left + Daily Goals */}
											{member?.task?.map((tsk) => {
												if (tsk?.year === currentYear) {
													const currentMonthData = tsk?.months?.find(
														(month) => month?.monthNumber === currentMonth
													);
													return currentMonthData ? (
														<>
															<TableCell key={currentMonthData._id}>
																$ {currentMonthData?.goals}
															</TableCell>
															<TableCell>
																{(() => {
																	const goalDifference =
																		parseInt(personTotalProfit) - parseInt(currentMonthData?.goals);
																	return goalDifference < 0
																		? `-$${Math.abs(goalDifference)}`
																		: `$${goalDifference}`;
																})()}
															</TableCell>
															<TableCell key={currentMonthData._id}>
																${" "}
																{(currentMonthData?.goals / currentMonthData?.activeDays).toFixed(
																	2
																)}
															</TableCell>
														</>
													) : null;
												}
												return null;
											})}

											{/* Profit Today */}
											{(() => {
												personDailyProfit = 0;
												member?.loadsMonth?.forEach((load) => {
													const loadDate = dayjs(load.createdAt);
													if (
														loadDate.year() === currentYear &&
														loadDate.month() + 1 === currentMonth &&
														loadDate.day() === currentDate
													) {
														const profit = parseInt(load.customerRate) - parseInt(load.driverRate);
														personDailyProfit += profit;
														personDailyLoad += 1;
														orgProfitToday += profit;
														orgLoadToday += 1;
													}
												});

												return <TableCell>$ {personDailyProfit}</TableCell>; // Return the total profit after summing
											})()}

											{/* Left Today */}
											{member?.task?.map((tsk) => {
												if (tsk?.year === currentYear) {
													const currentMonthData = tsk?.months?.find(
														(month) => month?.monthNumber === currentMonth
													);
													return currentMonthData ? (
														<>
															<TableCell key={currentMonthData._id}>
																{(() => {
																	const todayProfitLeft =
																		personDailyProfit -
																		(
																			currentMonthData?.goals / currentMonthData?.activeDays
																		).toFixed(2);
																	return todayProfitLeft < 0
																		? `-$${Math.abs(todayProfitLeft)}`
																		: `$${todayProfitLeft}`;
																})()}
															</TableCell>
														</>
													) : null;
												}
												return null;
											})}

											{/* Loads Today */}
											<TableCell>{personDailyLoad}</TableCell>
											{/* Avg Profit per load */}
											<TableCell>
												{isNaN(personTotalLoad) || personTotalLoad === 0
													? "$0"
													: `$${(personTotalProfit / personTotalLoad).toFixed(2)}`}
											</TableCell>

											{/* Avg Per Day */}
											<TableCell>
												{isNaN(personDailyLoad) || personDailyLoad === 0
													? "$0"
													: `$${(personDailyProfit / personDailyLoad).toFixed(2)}`}
											</TableCell>

											{/* Pace */}
											{member?.task?.map((tsk) => {
												if (tsk?.year === currentYear) {
													const currentMonthData = tsk?.months?.find(
														(month) => month?.monthNumber === currentMonth
													);
													return currentMonthData ? (
														<>
															<TableCell key={currentMonthData._id}>
																{isNaN(personDailyLoad) || personDailyLoad === 0
																	? "$0"
																	: `$${(
																			(personDailyProfit / personDailyLoad) *
																			currentMonthData?.activeDays
																	  ).toFixed(2)}`}
															</TableCell>
														</>
													) : null;
												}
												return null;
											})}

											{(() => {
												personTotalProfit = 0;
												monthlyProfitColl = [];

												member?.loadsMonth?.forEach((load) => {
													const loadDate = dayjs(load.createdAt);
													if (loadDate.year() === currentYear) {
														const profit = parseInt(load.customerRate) - parseInt(load.driverRate);
														monthlyProfitColl.push(profit);
														personTotalProfit += profit;
													}
												});

												return (
													<TableCell>
														{Math.max(...monthlyProfitColl) < 0
															? "$0"
															: Math.max(...monthlyProfitColl)}
													</TableCell>
												); // Return the total profit after summing
											})()}
											<TableCell>$0</TableCell>
										</TableRow>
									))}

									<TableRow>
										<TableCell>
											<strong>{branch.title}</strong>
										</TableCell>
										<TableCell>
											<strong>${totalProfit}</strong>
										</TableCell>
										{/* <TableCell>
                      <strong>{totalLoads}</strong> 
                    </TableCell> */}
										<TableCell>
											<strong>${totalBranchGoals}</strong>
										</TableCell>
										<TableCell>
											<strong>
												{(() => {
													const gD = parseInt(totalProfit) - parseInt(totalBranchGoals);
													return gD < 0 ? `-$${Math.abs(gD)}` : `$${gD}`;
												})()}
											</strong>

											{/* <strong>{totalProfit - totalBranchGoals}</strong> */}
										</TableCell>
										<TableCell>
											<strong>${safeDivide(totalBranchGoals, totalActiveDays).toFixed(2)}</strong>
										</TableCell>
										<TableCell>
											<strong>${orgProfitToday}</strong>
										</TableCell>
										<TableCell>
											<strong>
												{/* {(orgProfitToday - totalBranchGoals / totalActiveDays).toFixed(2)} */}
												{(() => {
													const gD = (
														orgProfitToday - safeDivide(totalBranchGoals, totalActiveDays)
													).toFixed(2);
													return gD < 0 ? `-$${Math.abs(gD)}` : `$${gD}`;
												})()}
											</strong>
										</TableCell>
										<TableCell>
											<strong>{orgLoadToday}</strong>
										</TableCell>
										<TableCell>
											<strong>
												{isNaN(totalProfit) || totalLoads === 0
													? "$ 0"
													: `$ ${(totalProfit / totalLoads).toFixed(2)}`}
											</strong>
										</TableCell>
										<TableCell>
											<strong>
												{isNaN(totalProfit) || totalActiveDays === 0
													? "$ 0"
													: `$ ${(totalProfit / totalActiveDays).toFixed(2)}`}
											</strong>
										</TableCell>
										<TableCell>
											<strong>
												{isNaN(orgProfitToday) || orgLoadToday === 0
													? "$ 0"
													: `$${((orgProfitToday / orgLoadToday) * totalActiveDays).toFixed(2)}`}
											</strong>
										</TableCell>
										<TableCell>
											<strong>$0</strong>
										</TableCell>
										<TableCell>
											<strong>$0</strong>
										</TableCell>
									</TableRow>
								</React.Fragment>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

export default OrganizationBigTable;
